/** QuickPlan — Nomenclature, liste de débit et optimisation des barres.
 *
 * computeBom(project, lookup) parcourt les instances (masquées comprises : masquer
 * n'est qu'un confort de vue), regroupe :
 *   - pièces identiques (même composant + même désignation de dimensions),
 *   - débits identiques (même composant + même longueur),
 *   - quincaillerie des kits (par référence),
 *   - lignes libres du projet (extras).
 * Les débits sont ensuite placés dans des barres du commerce (premier ajustement
 * décroissant, trait de scie compris) pour estimer le nombre de barres à acheter.
 */
import { resolveComponent } from './resolve.js';
import { CATEGORIES } from './schema.js';

const r2 = (v) => Math.round(v * 100) / 100;
const dim = (v) => String(Math.round(v * 10) / 10).replace('.', ',');

const FIXING_NAMES = {
  vis: (d, l) => [`VIS-${dim(d)}x${dim(l)}`, `Vis Ø${dim(d)}×${dim(l)}`, 0.08],
  clou: (d, l) => [`CLOU-${dim(d)}x${dim(l)}`, `Clou Ø${dim(d)}×${dim(l)}`, 0.02],
  boulon: (d, l) => [`BOUL-M${dim(d)}x${dim(l)}`, `Boulon M${dim(d)}×${dim(l)} + écrou`, 0.3],
  cheville: (d, l) => [`CHEV-${dim(d)}x${dim(l)}`, `Cheville Ø${dim(d)}×${dim(l)}`, 0.1],
  tourillon: (d, l) => [`TOUR-${dim(d)}x${dim(l)}`, `Tourillon bois Ø${dim(d)}×${dim(l)}`, 0.03],
  rivet: (d, l) => [`RIV-${dim(d)}x${dim(l)}`, `Rivet aveugle Ø${dim(d)}×${dim(l)}`, 0.05],
  excentrique: (d) => [`EXC-${dim(d)}`, `Excentrique Ø${dim(d)} + tige`, 0.35],
  agrafe: (d, l) => [`AGR-${dim(l)}`, `Agrafe ${dim(l)} mm`, 0.01],
  percage: (d, l) => [`PERC-${dim(d)}x${dim(l)}`, `Perçage Ø${dim(d)} prof. ${dim(l)}`, 0],
};

/** Désignation de nomenclature d'un emplacement de fixation résolu. */
export function fixingDesignation(f) {
  const [ref, name, price] = (FIXING_NAMES[f.type] ?? FIXING_NAMES.vis)(f.d, f.l);
  return { ref: f.ref ?? ref, name: f.name ?? name, price: f.price ?? price };
}

export function computeBom(project, lookup) {
  const rows = new Map();
  const kit = new Map();
  const errors = [];
  const pieces = new Map(); // stock → [longueurs]

  const bump = (map, key, init, qty = 1) => {
    const row = map.get(key) ?? { ...init, qty: 0, instances: [] };
    row.qty += qty;
    map.set(key, row);
    return row;
  };

  for (const inst of project.instances ?? []) {
    const comp = lookup(inst.componentId);
    if (!comp) {
      errors.push(`${inst.name || inst.id} : composant « ${inst.componentId} » introuvable`);
      continue;
    }
    let res;
    try {
      res = resolveComponent(comp, inst.params);
    } catch (e) {
      errors.push(`${inst.name || inst.id} : ${e.message}`);
      continue;
    }
    const bom = comp.bom ?? { kind: 'piece' };
    if (bom.kind === 'cut' && res.length != null) {
      const stock = bom.stock || comp.name;
      const row = bump(rows, `${comp.id}|${res.length}`, {
        kind: 'cut',
        ref: comp.ref || comp.id,
        name: comp.name,
        category: comp.category,
        label: `${String(res.length).replace('.', ',')} mm`,
        length: res.length,
        stock,
        unitPrice: bom.pricePerM != null ? r2((bom.pricePerM * res.length) / 1000) : null,
        material: comp.material,
      });
      row.instances.push(inst.id);
      if (!pieces.has(stock)) pieces.set(stock, []);
      pieces.get(stock).push(res.length);
    } else if (bom.kind !== 'none') {
      const row = bump(rows, `${comp.id}|${res.label}`, {
        kind: 'piece',
        ref: comp.ref || comp.id,
        name: comp.name,
        category: comp.category,
        label: res.label,
        unitPrice: res.price,
        material: comp.material,
      });
      row.instances.push(inst.id);
    }
    for (const f of res.fixings ?? []) {
      if (f.type === 'percage') continue; // trou seul : rien à acheter
      const { ref, name, price } = fixingDesignation(f);
      const row = bump(kit, ref, { kind: 'kit', ref, name, category: 'quincaillerie', label: 'emplacement', unitPrice: price }, 1);
      row.instances.push(inst.id);
    }
    for (const k of res.kit) {
      const row = bump(kit, k.ref, { kind: 'kit', ref: k.ref, name: k.name, category: 'quincaillerie', label: '', unitPrice: k.price ?? null }, k.qty);
      row.instances.push(inst.id);
    }
  }

  const order = (c) => { const i = CATEGORIES.indexOf(c); return i < 0 ? 99 : i; };
  const main = [...rows.values()].sort((a, b) =>
    order(a.category) - order(b.category)
    || a.name.localeCompare(b.name, 'fr')
    || (b.length ?? 0) - (a.length ?? 0)
    || String(a.label).localeCompare(String(b.label), 'fr', { numeric: true }));
  const hardware = [...kit.values()].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  const extras = (project.extras ?? []).map((x) => ({
    kind: 'extra', id: x.id, ref: x.ref, name: x.name, category: 'autre', label: '', unit: x.unit, qty: x.qty, unitPrice: x.price ?? null, instances: [],
  }));

  const all = [...main, ...hardware, ...extras].map((r) => ({
    ...r,
    qty: r2(r.qty),
    total: r.unitPrice != null ? r2(r.unitPrice * r.qty) : null,
  }));

  const s = project.settings ?? {};
  const cuts = [...pieces.entries()].map(([stock, lengths]) => ({
    stock,
    ...packBars(lengths, s.barLength ?? 6000, s.kerf ?? 3),
  })).sort((a, b) => a.stock.localeCompare(b.stock, 'fr'));

  const total = r2(all.reduce((acc, r) => acc + (r.total ?? 0), 0));
  const unpriced = all.filter((r) => r.unitPrice == null).length;
  return { rows: all, cuts, total, unpriced, errors };
}

/** Rangement des débits en barres : premier ajustement décroissant.
 *  Une pièce plus longue qu'une barre occupe sa propre « barre » (signalée). */
export function packBars(lengths, barLength, kerf) {
  const sorted = [...lengths].sort((a, b) => b - a);
  const bars = [];
  const oversize = [];
  for (const len of sorted) {
    if (len > barLength) { oversize.push(len); continue; }
    let bar = bars.find((b) => b.used + len + (b.pieces.length ? kerf : 0) <= barLength);
    if (!bar) { bar = { used: 0, pieces: [] }; bars.push(bar); }
    bar.used += len + (bar.pieces.length ? kerf : 0);
    bar.pieces.push(len);
  }
  const totalLength = lengths.reduce((a, b) => a + b, 0);
  return {
    count: lengths.length,
    totalLength: r2(totalLength),
    barLength,
    bars: bars.map((b) => ({ pieces: b.pieces, used: r2(b.used), offcut: r2(barLength - b.used) })),
    oversize,
    usage: bars.length ? r2((100 * lengths.filter((l) => l <= barLength).reduce((a, b) => a + b, 0)) / (bars.length * barLength)) : 0,
  };
}

const num = (v) => (v == null ? '' : String(v).replace('.', ','));
const cell = (v) => {
  const s = String(v ?? '');
  return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** CSV « Excel France » : séparateur ;, décimales à virgule, BOM UTF-8. */
export function bomToCsv(bom, projectName = '') {
  const lines = [];
  lines.push(['Nomenclature', projectName].map(cell).join(';'));
  lines.push(['Type', 'Référence', 'Désignation', 'Dimensions', 'Matière', 'Qté', 'Prix unitaire €', 'Total €'].join(';'));
  const kinds = { piece: 'Pièce', cut: 'Débit', kit: 'Quincaillerie', extra: 'Divers' };
  for (const r of bom.rows) {
    lines.push([kinds[r.kind], r.ref, r.name, r.label, r.material ?? '', num(r.qty), num(r.unitPrice), num(r.total)].map(cell).join(';'));
  }
  lines.push(['', '', '', '', '', '', 'Total', num(bom.total)].join(';'));
  if (bom.cuts.length) {
    lines.push('');
    lines.push(['Débit', 'Barres', 'Longueur barre', 'Pièces', 'Longueur totale', 'Rendement %'].join(';'));
    for (const c of bom.cuts) {
      lines.push([c.stock, c.bars.length, c.barLength, c.count, num(c.totalLength), num(c.usage)].map(cell).join(';'));
      c.bars.forEach((b, i) => lines.push([`  barre ${i + 1}`, '', '', b.pieces.join(' + '), num(b.used), `chute ${num(b.offcut)}`].map(cell).join(';')));
    }
  }
  return '﻿' + lines.join('\r\n');
}
